import { featureEach } from '@turf/meta';
import { fromUrl } from 'geotiff';
import proj4 from 'proj4';

proj4.defs('EPSG:4326', '+proj=longlat +datum=WGS84 +no_defs +type=crs');

// EPSG:27700 needs a nadgrid file, so we keep it hardcoded with the grid reference
proj4.defs(
  'EPSG:27700',
  '+proj=tmerc +lat_0=49 +lon_0=-2 +k=0.9996012717 +x_0=400000 +y_0=-100000 +ellps=airy +nadgrids=uk_os_OSTN15_NTv2_OSGBtoETRS.tif +units=m +no_defs +type=crs',
);

const nadgridPromise = fromUrl('/uk_os_OSTN15_NTv2_OSGBtoETRS.tif').then(
  (tiff) => proj4.nadgrid('uk_os_OSTN15_NTv2_OSGBtoETRS.tif', tiff).ready,
);
// Started at import time, so a failed fetch would otherwise be an unhandled
// rejection whenever nothing has awaited it yet (fails CI's vitest run, where
// no server answers). Callers that `await nadgridPromise` still get the error.
nadgridPromise.catch(() => {});

/**
 * Register a projection definition from the server's spatial_ref_sys table.
 * Safe to call multiple times — skips if already defined or if proj4String is null.
 */
export const registerProjection = (srid: number, proj4String: string | null | undefined) => {
  if (!proj4String || srid === -1) return;
  const name = `EPSG:${srid}`;
  // Skip if already registered (e.g. 27700 with nadgrid)
  if (proj4.defs(name)) return;
  proj4.defs(name, proj4String);
};

export const reprojectGeoJSON = async (geojson: any, from = 'EPSG:27700', to = 'EPSG:4326') => {
  await nadgridPromise;

  geojson = JSON.parse(JSON.stringify(geojson));

  featureEach(geojson, (feature) => {
    feature.geometry.coordinates = traverseAndProject(feature.geometry.coordinates, from, to);
  });
  return geojson;
};

export const project = async (coords: any, from = 'EPSG:27700', to = 'EPSG:4326') => {
  await nadgridPromise;

  if (!proj4.defs(from)) return console.warn(`${from} not loaded yet.`);

  if (typeof coords[0] === 'number') {
    return proj4(from, to, coords);
  }
  return coords.map((c) => project(c));
};

export const traverseAndProject = (coords: any, from: string, to: string) => {
  if (typeof coords[0] === 'number') {
    return proj4(from, to, coords);
  }
  return coords.map((c) => traverseAndProject(c, from, to));
};
